import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ChevronDown, ChevronUp, Search, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

const categories = [
  { id: 'all', name: 'All' },
  { id: 'cognitive', name: 'Cognitive', color: 'bg-purple-100 text-purple-700' },
  { id: 'autoimmune', name: 'Autoimmune', color: 'bg-rose-100 text-rose-700' },
  { id: 'mental', name: 'Mental Health', color: 'bg-blue-100 text-blue-700' },
  { id: 'pain', name: 'Chronic Pain', color: 'bg-amber-100 text-amber-700' },
  { id: 'sensory', name: 'Sensory', color: 'bg-teal-100 text-teal-700' },
  { id: 'dynamic', name: 'Dynamic', color: 'bg-indigo-100 text-indigo-700' },
];

function ConditionCard({ condition, onSelect }) {
  const [expanded, setExpanded] = useState(false);
  const category = categories.find(c => c.id === condition.category);
  
  return (
    <div className="card-soft">
      <button onClick={() => setExpanded(!expanded)} className="w-full text-left">
        <div className="flex items-center justify-between">
          <div>
            <h4 className="font-medium text-foreground">{condition.name}</h4>
            <span className={`text-xs px-2 py-0.5 rounded-full ${category?.color || 'bg-gray-100'}`}>
              {condition.categoryName}
            </span>
          </div>
          {expanded ? <ChevronUp className="h-5 w-5 text-muted-foreground" /> : <ChevronDown className="h-5 w-5 text-muted-foreground" />}
        </div>
      </button>
      
      {expanded && (
        <div className="mt-4 space-y-4">
          <p className="text-muted-foreground text-sm">{condition.description}</p>
          
          <div>
            <h5 className="text-sm font-medium text-foreground mb-2">Common Experiences:</h5>
            <ul className="space-y-1 text-sm text-muted-foreground">
              {condition.symptoms.split(', ').map((s, i) => (
                <li key={i} className="flex items-start gap-2">
                  <span className="text-primary">•</span>
                  <span>{s}</span>
                </li>
              ))}
            </ul>
          </div>
          
          <div className="bg-secondary/50 rounded-xl p-4">
            <h5 className="text-sm font-medium text-foreground mb-1">Disclosure Tip:</h5>
            <p className="text-sm text-muted-foreground">{condition.tips}</p>
          </div>
          
          <div className="flex gap-2 pt-2">
            <Button size="sm" onClick={() => onSelect(condition)} className="flex-1">
              Get Scripts
            </Button>
            <Button size="sm" variant="outline" onClick={() => window.location.href = `/disclosure-card/${condition.id}`}>
              Print Card
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function Conditions() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [allConditions, setAllConditions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch('/conditionsList.json')
      .then(res => res.json())
      .then(data => {
        setAllConditions(data);
        setLoading(false);
      })
      .catch(err => {
        console.error('Failed to load conditions:', err);
        setLoading(false);
      });
  }, []);

  const filteredConditions = allConditions.filter(condition => {
    const matchesCategory = selectedCategory === 'all' || condition.category === selectedCategory;
    const query = searchQuery.toLowerCase();
    const matchesSearch = searchQuery === '' || 
      condition.name.toLowerCase().includes(query) ||
      condition.description.toLowerCase().includes(query) ||
      condition.keywords.toLowerCase().includes(query) ||
      condition.symptoms.toLowerCase().includes(query);
    return matchesCategory && matchesSearch;
  });

  const handleSelectCondition = (condition) => {
    navigate('/scenario/workplace', { state: { conditionContext: condition } });
  };

  return (
    <div className="min-h-screen pb-safe">
      <div className="max-w-lg mx-auto px-6 pt-8">
        <button 
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
          data-testid="back-btn"
        >
          <ArrowLeft className="h-5 w-5" />
          <span>Back</span>
        </button>

        <div className="mt-8 page-enter">
          <h1 className="font-heading text-3xl font-semibold text-foreground">Conditions Guide</h1>
          <p className="mt-2 text-muted-foreground">Search and explore invisible disabilities</p>
        </div>

        {/* Not a complete list notice */}
        <div className="mt-4 card-soft bg-blue-50 border-blue-200 page-enter stagger-1">
          <p className="text-sm text-blue-800">
            <strong>This is not a complete list.</strong> There are many invisible and dynamic disabilities not shown here. If your condition isn't listed, the tools in this app can still help you track symptoms, prepare for conversations, and find the right words.
          </p>
        </div>

        <div className="mt-6 relative page-enter stagger-1">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
          <Input
            data-testid="condition-search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search conditions, symptoms..."
            className="pl-12 pr-10 h-12"
          />
          {searchQuery && (
            <button 
              onClick={() => setSearchQuery('')}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              <X className="h-5 w-5" />
            </button>
          )}
        </div>

        <div className="mt-4 flex gap-2 overflow-x-auto pb-2 page-enter stagger-1">
          {categories.map((cat) => (
            <button
              key={cat.id}
              onClick={() => setSelectedCategory(cat.id)}
              className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                selectedCategory === cat.id
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-secondary text-secondary-foreground hover:bg-secondary/80'
              }`}
            >
              {cat.name}
            </button>
          ))}
        </div>

        <div className="mt-4 text-sm text-muted-foreground">
          {loading ? 'Loading...' : `Showing ${filteredConditions.length} condition${filteredConditions.length !== 1 ? 's' : ''}`}
        </div>

        <div className="mt-4 space-y-3">
          {loading ? (
            <div className="card-soft text-center py-8">
              <p className="text-muted-foreground">Loading conditions...</p>
            </div>
          ) : filteredConditions.length === 0 ? (
            <div className="card-soft text-center py-8">
              <p className="text-muted-foreground">No conditions found matching your search.</p>
              <button 
                onClick={() => { setSearchQuery(''); setSelectedCategory('all'); }}
                className="text-primary mt-2 hover:underline"
              >
                Clear filters
              </button>
            </div>
          ) : (
            filteredConditions.map((condition) => (
              <ConditionCard 
                key={condition.id} 
                condition={condition} 
                onSelect={handleSelectCondition}
              />
            ))
          )}
        </div>

        <div className="mt-8 card-soft bg-muted">
          <p className="text-xs text-muted-foreground">
            <strong className="text-foreground">Note:</strong> This information is for educational purposes only.
          </p>
        </div>

        <div className="h-8" />
      </div>
    </div>
  );
}
